/**
 * The in-season cycle scope — where `run_id` comes from, and why it has no default.
 *
 * `GET /inseason/{proposals,price-actions,transfers}` all take `run_id`, and the
 * platform's own docstring on `list_proposals` records why: without it the endpoint
 * "answered from an empty reader for three milestones", because
 * `inseason.proposals_statement` takes a run and a scope and a route that named neither
 * could not call it. G16 puts the same rule the other way round — a read whose perimeter
 * is not stated is a read over the whole estate.
 *
 * So this module has **no fallback run identifier**. A screen with no run in its URL does
 * not issue the request at all. Picking "the latest run" here would look helpful and would
 * be exactly the unbounded read the `run_id` parameter was added to close. (The owner-ruled
 * default — ADR 6011 for the Pricing pages, generalised to every run-scoped page by DEC-99,
 * `docs/adr/9039` — lives in `./default-run.tsx` and `CycleRunPicker`, not here: the newest
 * run of the page's kind is chosen from the published directory and
 * written INTO the URL, so every read is still bounded by a run the URL names.)
 *
 * **The second half of that argument has expired; the first half has not.** This paragraph
 * used to end "and there is no endpoint that publishes the run directory a real picker
 * would need (reported in ADR 0051)", which is why a screen with no run rendered a designed
 * state naming the missing parameter and nothing else. `GET /inseason/runs` now publishes
 * that directory and `CycleRunPicker` offers it. Nothing is defaulted, no read is issued
 * before a planner has chosen, and the URL is still the state — what changed is that
 * reaching a run-bounded screen no longer requires already knowing a `run_id`.
 *
 * The URL is the carrier because G2.2 makes it the carrier: *"THE URL IS THE STATE. Any
 * screen, filter set, drill path and scenario is a shareable, bookmarkable link."* The
 * shell's own `readUrl` lives in `src/app/routes/router.tsx` and `no-restricted-imports`
 * forbids importing it from a feature (G11 dependency direction: the shell composes
 * features, not the reverse), so the search string is read directly here. That is a
 * six-line duplication of a parse, not a duplicated component.
 */

import { useEffect, useMemo, useState } from 'react';

import type { ObjectiveKind, PenaltyBasis } from '@/api/generated';

import { isObjectiveKind, isPenaltyBasis } from './markdown';

/** The cycle run the reads are bounded by. `?runId=…`. */
export const RUN_ID_PARAM = 'runId';

/**
 * The date a gate verdict is read *as at*. `?asOf=…`.
 *
 * `list_d2_evidence` is dated rather than current on purpose — "a gate verdict is a claim
 * about a moment: re-reading it today and re-reading it at the review are the same query
 * only if the date is an argument (G9)". Omitting it asks the service for its own default
 * as-at, which is a documented server behaviour rather than a client-invented one.
 */
export const AS_OF_PARAM = 'asOf';

/**
 * What `GET /inseason/markdown/path` needs beyond the as-at date, all from the URL.
 *
 * Eight parameters is a lot for one link, and the count is a symptom rather than a
 * design. Two of them — `weeksRemaining` and `minorUnit` — are on the request
 * because the retail calendar and `13`'s currency reference are not in the plan
 * store, so the API cannot read them either; the platform's own docstring calls
 * them gaps with names and says each is removed when its feed arrives rather than
 * defaulted. They are carried here for the same reason `runId` has no fallback: a
 * screen supplying its own horizon would be posing a different problem from the
 * one the answer claims to have solved.
 *
 * **`openingUnits` used to be the ninth and is no longer required**, because the
 * platform resolves it. bata-data's B1 `stock_position` feed put the daily
 * position in the plan store, so `GET /markdown/path` now sums the option's
 * sellable on-hand units itself when the parameter is absent and reports
 * `provenance.opening_units` as `MEASURED`. It stays *accepted* — see
 * {@link MARKDOWN_PATH_OPTIONAL_PARAMS} — rather than being deleted, because a
 * caller stating its own position is asking a different question and the platform
 * answers that one too.
 *
 * **`optionKey` became `optionKeys`, and the plural is the whole point.** The
 * route answers for one option and there is no batch endpoint, so a planner with
 * a few hundred options to review had to visit a few hundred links. The scalar
 * parameters below are the ones a batch genuinely shares — one market, one
 * cluster, one channel, one horizon, one as-at date — and the option list is the
 * axis it varies over. Splitting them this way is why the screen can ask N
 * questions without inventing anything: every request it issues differs from its
 * neighbour in exactly one value the planner chose.
 */
export const MARKDOWN_PATH_SCALAR_PARAMS = [
  'countryKey',
  'brandKey',
  'clusterKey',
  'channelKey',
  'modelVersionId',
  'weeksRemaining',
  'minorUnit',
] as const;

/** The option list, comma-separated. `?optionKeys=001000KW,001000LR`. */
export const OPTION_KEYS_PARAM = 'optionKeys';

/**
 * The singular spelling, still read when `optionKeys` is absent.
 *
 * Every link a planner has bookmarked, and every link in a ticket or a message,
 * spells it `optionKey`. Dropping it would break those silently — the screen
 * would render its own "which option?" empty state rather than an error, which
 * is the worst of the three possible behaviours. So the plural wins where both
 * appear and the singular is read where it is alone.
 */
export const OPTION_KEY_PARAM_LEGACY = 'optionKey';

/**
 * Every required parameter name, for the empty state that lists what is absent.
 *
 * `optionKeys` sits where `optionKey` sat so that a planner reading the list of
 * missing parameters sees them in the order the picker above asks for them.
 */
export const MARKDOWN_PATH_PARAMS = [
  'countryKey',
  'brandKey',
  OPTION_KEYS_PARAM,
  'clusterKey',
  'channelKey',
  'modelVersionId',
  'weeksRemaining',
  'minorUnit',
] as const;

/**
 * Parameters the read accepts and does not need.
 *
 * Kept as its own list rather than as a boolean on each name: the all-or-nothing
 * rule below is about the *required* set, and folding an optional parameter into
 * it would either make the link demand something the platform can supply or make
 * `missing` name something whose absence is not a problem. One planner supplying
 * a position by hand and another leaving it to the estate are both complete
 * requests, and this is the only place that distinction is written down.
 */
export const MARKDOWN_PATH_OPTIONAL_PARAMS = ['openingUnits'] as const;

/**
 * What the optimiser is asked to maximise. `?objectiveKind=`, `?salvageFraction=`,
 * `?penaltyBasis=`.
 *
 * All three are **accepted query parameters on both `/markdown/path` and
 * `/markdown/frontier` and were never sent**, so every path this screen has ever drawn
 * was solved under whichever objective the platform's registry holds — which is the right
 * answer, and was invisible. reqs/04 §4.4 makes the choice between them a commercial one:
 * `terminal_constraint` treats chapter 05's terminal-stock target as a hard feasibility
 * filter, and `leftover_penalty` prices the leftover instead. The two produce different
 * recommendations from the same position, and which one a market runs under is a decision
 * with an owner.
 *
 * **Absent means absent, not "the first one".** An unstated objective sends no parameter
 * at all, which asks the platform for its own governed objective and is a different
 * request from naming one. A picker that opened on `terminal_constraint` would state a
 * commercial policy the planner never chose, and would state it in a shareable link. That
 * is the same rule `weeksRemaining` follows and for the same reason.
 *
 * **The platform may refuse the objective a planner names** — `objective_not_permitted` —
 * because which objectives a market may run under is governed rather than offered. That
 * refusal renders through the problem catalogue like every other, and the screen keeps the
 * choice on the URL so the refused question stays visible beside the refusal.
 */
export const OBJECTIVE_KIND_PARAM = 'objectiveKind';
export const SALVAGE_FRACTION_PARAM = 'salvageFraction';
export const PENALTY_BASIS_PARAM = 'penaltyBasis';

/**
 * A second objective to solve the same option under, for a side-by-side. `?compareKind=`.
 *
 * Only the kind, and deliberately: a comparison of two objectives at two salvage
 * fractions is four changes at once and answers nothing. The comparison holds the
 * position, the horizon and the ladder fixed — every one of them a scalar on the same
 * URL — so exactly one thing differs between the two answers, which is what makes them
 * comparable at all.
 */
export const COMPARE_KIND_PARAM = 'compareKind';

/**
 * Which panel of the old one-page Output the planner was looking at. `?markdownView=`. RETIRED.
 *
 * It existed so one export could tell which of four panels it was taken from (ADR 0058). Issue 226
 * (giro 3 «Dopo» point 1) made each of those panels a page with its own export — Result, the run's
 * Calendar, the Frontier — so the page IS the view and nothing reads this any more. The name stays
 * only so an old link that carries it is moved to the page that now holds its panel
 * (`legacyOutputRedirect`, `markdown-views.tsx`) and then loses it.
 */
export const MARKDOWN_VIEW_PARAM = 'markdownView';

/**
 * The option of a several-option question whose answer is open on Result. `?openOption=`.
 *
 * Issue 226: with several options posed, Result opens on «Options solved», and a row opens THAT
 * option's answer — a drill, so the list stays on the URL (`optionKeys`) and the breadcrumb climbs
 * back to it by removing this key. It used to overwrite `optionKeys` with the one key, which lost
 * the list. Never defaulted: no option is opened for the planner.
 */
export const OPEN_OPTION_PARAM = 'openOption';

/** The open option, or `null` — only a member of a several-option question. Pure. */
export function readOpenOption(search: string, optionKeys: readonly string[]): string | null {
  if (optionKeys.length < 2) return null;
  const raw = new URLSearchParams(search).get(OPEN_OPTION_PARAM)?.trim() ?? '';
  return optionKeys.includes(raw) ? raw : null;
}

export interface MarkdownObjective {
  readonly kind: ObjectiveKind;
  /** `null` where the URL names none; never defaulted to a fraction. */
  readonly salvageFraction: string | null;
  /** `null` where the URL names none, and meaningless unless `kind` is a leftover penalty. */
  readonly penaltyBasis: PenaltyBasis | null;
}

export type MarkdownPathParamName = (typeof MARKDOWN_PATH_PARAMS)[number];
export type MarkdownPathScalarParamName = (typeof MARKDOWN_PATH_SCALAR_PARAMS)[number];
export type MarkdownPathOptionalParamName = (typeof MARKDOWN_PATH_OPTIONAL_PARAMS)[number];

export interface MarkdownPathScope {
  /**
   * Every required scalar parameter present and non-blank, or `null`. Never partial.
   *
   * `null` whenever {@link optionKeys} is empty too: one option with no market is
   * as unanswerable as a market with no options, and a screen that issued the
   * first would put a platform refusal on the page for something the URL could
   * have been checked for.
   */
  readonly values: Readonly<Record<MarkdownPathScalarParamName, string>> | null;
  /**
   * The options to solve, de-duplicated, in the order the URL names them.
   *
   * Order is the planner's, so the results table can be read against the list
   * they pasted. De-duplicated because asking the same question twice costs a
   * round trip and renders a row that looks like a second finding.
   */
  readonly optionKeys: readonly string[];
  /** Which required parameters the URL does not name, in declaration order. */
  readonly missing: readonly MarkdownPathParamName[];
  /**
   * The stated opening position, or `null` to let the platform resolve it.
   *
   * `null` is not "missing". It is the request that asks the estate for the
   * position, which is the one a planner should normally make.
   */
  readonly openingUnits: string | null;
  /**
   * The objective the caller stated, or `null` to let the platform use its governed one.
   *
   * `null` is not "missing" — it is the request that asks the platform which objective is
   * in force, which is the one a planner should normally make. See
   * {@link OBJECTIVE_KIND_PARAM}.
   */
  readonly objective: MarkdownObjective | null;
  /** A second objective kind to solve the same option under, or `null`. */
  readonly compareKind: ObjectiveKind | null;
}

export interface InSeasonScope {
  /** `null` where the URL does not name a run. Never substituted. */
  readonly runId: string | null;
  /** `null` where the URL does not name an as-at date. Never substituted. */
  readonly asOf: string | null;
  /** The markdown path read's own parameters, all or nothing. */
  readonly markdownPath: MarkdownPathScope;
}

/**
 * The "why this" view (bata-ui#148 step 3.3, `docs/adr/6005`). `?explain=<id>` opens the
 * explanation of ONE queued item — a proposal on the allocation screen, a price step on the
 * markdown Output view, a transfer on the rebalancing screen — and `?view=explain` opens the
 * queue's explain view with no item chosen yet, which is where the Cockpit's Explain entries
 * land. Both are URL state (G2.2): an explanation is a shareable link.
 *
 * **Neither picks an item.** `?view=explain` lists the queue's lines in the order the read
 * returned them and waits for the planner; opening "the first one" on their behalf would be
 * a choice nobody made, presented as the thing they asked about.
 */
/**
 * The frontier strategy card the planner chose on the Frontier (designer item 5.1, issue 226): a carrying-cost
 * rate, as the frontier publishes it. Never defaulted, and never read as a recommendation — it
 * marks the planner's choice on the link and the chart, and asks the platform nothing (the path
 * read takes no rate).
 */
export const CARRY_PARAM = 'carry';

/** The chosen rate, or `null` where the link names none or names something that is not a number. */
export function readCarry(search: string): number | null {
  const raw = new URLSearchParams(search).get(CARRY_PARAM)?.trim() ?? '';
  if (raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export const EXPLAIN_PARAM = 'explain';
export const EXPLAIN_VIEW_PARAM = 'view';
export const EXPLAIN_VIEW = 'explain';

export interface ExplainScope {
  /** The item whose explanation is open, or `null`. Never substituted. */
  readonly itemId: string | null;
  /** True when the queue's explain view is open — with an item or without one. */
  readonly open: boolean;
}

/** Pure parse of the explain state. */
export function readExplain(search: string): ExplainScope {
  const params = new URLSearchParams(search);
  const raw = params.get(EXPLAIN_PARAM)?.trim() ?? '';
  const itemId = raw === '' ? null : raw;
  return { itemId, open: itemId !== null || params.get(EXPLAIN_VIEW_PARAM) === EXPLAIN_VIEW };
}

/** The search string with the explain state set to `itemId` (or to the queue view on `null`). */
export function explainSearch(search: string, itemId: string | null): URLSearchParams {
  const next = new URLSearchParams(search);
  if (itemId === null) next.delete(EXPLAIN_PARAM);
  else next.set(EXPLAIN_PARAM, itemId);
  next.set(EXPLAIN_VIEW_PARAM, EXPLAIN_VIEW);
  return next;
}

/** The search string with the explain state removed — the view closed. */
export function closedExplainSearch(search: string): URLSearchParams {
  const next = new URLSearchParams(search);
  next.delete(EXPLAIN_PARAM);
  next.delete(EXPLAIN_VIEW_PARAM);
  return next;
}

/** Pure parse, so the test asserts on a string rather than on `window`. */
export function readScope(search: string): InSeasonScope {
  const params = new URLSearchParams(search);
  const runId = params.get(RUN_ID_PARAM);
  const asOf = params.get(AS_OF_PARAM);
  const resolvedAsOf = asOf !== null && asOf.trim() !== '' ? asOf : null;
  const markdownPath = readMarkdownPathScope(params, resolvedAsOf);
  return {
    runId: runId !== null && runId.trim() !== '' ? runId : null,
    asOf: resolvedAsOf,
    markdownPath,
  };
}

/**
 * The objective the URL states, or `null`.
 *
 * `null` unless a recognised `objectiveKind` is present: a URL carrying a salvage
 * fraction and no kind is not three-quarters of an objective, it is a link whose
 * objective was never stated, and sending the fraction alone would ask the platform to
 * price a leftover under an objective that may not charge for one. An UNRECOGNISED kind
 * is `null` too, for the reason `MARKDOWN_PATH_SCALAR_PARAMS` refuses partial scopes:
 * issuing it would put a 422 on screen for something the URL could have been checked for.
 *
 * The salvage fraction stays a string. It reaches the request as a number and the parse
 * belongs where the request is built, so a link carrying `salvageFraction=abc` produces a
 * scope that says what the URL says rather than a silent `NaN` three layers down.
 */
export function readObjective(params: URLSearchParams): MarkdownObjective | null {
  const rawKind = params.get(OBJECTIVE_KIND_PARAM)?.trim() ?? '';
  if (rawKind === '' || !isObjectiveKind(rawKind)) return null;
  const rawSalvage = params.get(SALVAGE_FRACTION_PARAM)?.trim() ?? '';
  const rawBasis = params.get(PENALTY_BASIS_PARAM)?.trim() ?? '';
  return {
    kind: rawKind,
    salvageFraction: rawSalvage === '' ? null : rawSalvage,
    penaltyBasis: isPenaltyBasis(rawBasis) ? rawBasis : null,
  };
}

/**
 * All of them or none of them.
 *
 * Partial is not a weaker version of complete here: a request missing one
 * parameter is not a narrower question, it is a 422, and issuing it would put a
 * platform refusal on screen for something the URL could have been checked for
 * first. `missing` travels alongside so the empty state names every absent
 * parameter at once rather than sending a planner round the loop nine times.
 *
 * `asOf` is shared with the D2 gate read rather than duplicated under a
 * markdown-specific name: both mean "as at which date", and two spellings of one
 * date in one URL is how a screen comes to show two moments at once.
 */
function readMarkdownPathScope(
  params: URLSearchParams,
  asOf: string | null,
): MarkdownPathScope {
  const values: Partial<Record<MarkdownPathScalarParamName, string>> = {};
  const missing: MarkdownPathParamName[] = [];
  const optionKeys = readOptionKeys(params);
  for (const name of MARKDOWN_PATH_PARAMS) {
    if (name === OPTION_KEYS_PARAM) {
      if (optionKeys.length === 0) missing.push(name);
      continue;
    }
    const raw = params.get(name);
    if (raw !== null && raw.trim() !== '') values[name] = raw.trim();
    else missing.push(name);
  }
  const stated = params.get('openingUnits');
  const openingUnits = stated !== null && stated.trim() !== '' ? stated.trim() : null;
  const objective = readObjective(params);
  const rawCompare = params.get(COMPARE_KIND_PARAM)?.trim() ?? '';
  // A comparison against the objective already chosen is not a comparison, so it is
  // dropped rather than rendered as two identical panels.
  const compareKind =
    isObjectiveKind(rawCompare) && rawCompare !== objective?.kind ? rawCompare : null;
  if (asOf === null || missing.length > 0) {
    return { values: null, optionKeys, missing, openingUnits, objective, compareKind };
  }
  return {
    values: values as Record<MarkdownPathScalarParamName, string>,
    optionKeys,
    missing: [],
    openingUnits,
    objective,
    compareKind,
  };
}

/**
 * The objective as the two reads take it, ready to spread into a query.
 *
 * `exactOptionalPropertyTypes` is on, so an explicitly-`undefined` property is a type
 * error rather than an omission — every absent value has to be absent from the object,
 * which is why this returns a partial rather than a record with nulls in it. That is also
 * exactly the wire behaviour wanted: an unstated objective sends no parameter, and the
 * platform answers under the objective its registry holds.
 */
export function objectiveQuery(objective: MarkdownObjective | null): {
  readonly objective_kind?: ObjectiveKind;
  readonly salvage_fraction?: number;
  readonly penalty_basis?: PenaltyBasis;
} {
  if (objective === null) return {};
  const salvage = objective.salvageFraction === null ? null : Number(objective.salvageFraction);
  return {
    objective_kind: objective.kind,
    // A fraction the URL spells unparseably is omitted rather than sent as `NaN`, which
    // would serialise as the literal string `NaN` and be refused as a validation error
    // naming a field the planner never typed.
    ...(salvage === null || Number.isNaN(salvage) ? {} : { salvage_fraction: salvage }),
    ...(objective.penaltyBasis === null ? {} : { penalty_basis: objective.penaltyBasis }),
  };
}

/**
 * The single-option markdown read's query, split into the part every read shares and the
 * objective — or `null` until the URL names every parameter.
 *
 * Built once here because two views now ask it: the Output view solves the path, and the
 * Explanation view shows the same path week by week as the elasticity's effect over time. The
 * same object gives the same React Query key, so the second view is served from the first's
 * answer rather than asking the platform again. The comparison read keeps `base` and swaps
 * the objective, which is why the two halves are returned apart.
 *
 * `opening_units` is omitted unless the URL states one: absent, the platform resolves the
 * option's sellable position and says MEASURED; `Number(null)` would be a third thing — zero —
 * which is a clearance for an option nobody has established is empty.
 */
export function markdownPathQuery(scope: InSeasonScope, optionKey?: string): {
  readonly base: {
    readonly country_key: string;
    readonly brand_key: string;
    readonly option_key: string;
    readonly cluster_key: string;
    readonly channel_key: string;
    readonly model_version_id: string;
    readonly as_of: string;
    readonly weeks_remaining: number;
    readonly minor_unit: number;
    readonly opening_units?: number;
  };
  readonly objective: ReturnType<typeof objectiveQuery>;
} | null {
  const { markdownPath, asOf } = scope;
  if (markdownPath.values === null) return null;
  return {
    base: {
      country_key: markdownPath.values.countryKey,
      brand_key: markdownPath.values.brandKey,
      option_key: optionKey ?? markdownPath.optionKeys[0] ?? '',
      cluster_key: markdownPath.values.clusterKey,
      channel_key: markdownPath.values.channelKey,
      model_version_id: markdownPath.values.modelVersionId,
      as_of: asOf ?? '',
      weeks_remaining: Number(markdownPath.values.weeksRemaining),
      minor_unit: Number(markdownPath.values.minorUnit),
      ...(markdownPath.openingUnits === null
        ? {}
        : { opening_units: Number(markdownPath.openingUnits) }),
    },
    objective: objectiveQuery(markdownPath.objective),
  };
}

/**
 * The option list, from the plural parameter or the singular one it replaced.
 *
 * Splitting on a comma rather than repeating the parameter keeps a three-hundred
 * option link readable and, more to the point, short: a repeated parameter is the
 * other conventional spelling and it multiplies the URL's length by the name.
 * Blank entries are dropped rather than refused, because a trailing comma in a
 * pasted list is a typing artefact and not a question about an option with no
 * key.
 */
function readOptionKeys(params: URLSearchParams): readonly string[] {
  const raw = params.get(OPTION_KEYS_PARAM) ?? params.get(OPTION_KEY_PARAM_LEGACY);
  if (raw === null) return [];
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const part of raw.split(',')) {
    const key = part.trim();
    if (key === '' || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

/**
 * Replace the address bar with `next`, and let the screens re-read it.
 *
 * **Here rather than in the shell's router, and that is not a duplication.**
 * `no-restricted-imports` forbids a feature importing `src/app/routes/router.tsx`
 * (G11 dependency direction: the shell composes features, not the reverse), so a
 * feature that needs to navigate has to reach `history` itself. The six lines
 * below are that, and the comment in this module's header covers why the parse
 * half is duplicated for the same reason.
 *
 * **The `popstate` dispatch is load-bearing.** `pushState` fires no event, and
 * `useInSeasonScope` recomputes from `window.location` on render plus on
 * `popstate`. Without the dispatch a picker would change the URL, the queries
 * would keep their old parameters, and the screen would show one scope while the
 * address bar showed another — the two-moments failure this module's header
 * warns about, arriving through navigation rather than through two date params.
 *
 * `pushState`, not `replaceState`: G2.2 makes the URL the state, and a planner
 * who narrows a scope and then wants the previous one back should get it from the
 * browser's own back button rather than by retyping.
 */
export function navigateToScope(next: URLSearchParams, pathname?: string): void {
  if (typeof window === 'undefined') return;
  const query = next.toString();
  // `pathname` moves the question to another view of it — the markdown's Solve lands on its
  // Output — and the query travels unchanged, because the scope IS the question.
  if (pathname === undefined) {
    window.history.pushState({}, '', query === '' ? window.location.pathname : `?${query}`);
  } else {
    window.history.pushState({}, '', query === '' ? pathname : `${pathname}?${query}`);
  }
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Write a scope the planner did not choose into the URL WITHOUT a history entry.
 *
 * The caller is the default run (ADR 6011, DEC-99 — `./default-run.tsx`): the page opened with no
 * run, one was chosen for the planner, and Back must leave the page rather than return to the
 * no-run address and have the same run chosen again.
 */
export function replaceScope(next: URLSearchParams): void {
  if (typeof window === 'undefined') return;
  const query = next.toString();
  window.history.replaceState(window.history.state, '', query === '' ? window.location.pathname : `?${query}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * The current scope, following browser back and forward.
 *
 * `popstate` alone is not enough: the shell navigates with `history.pushState`, which
 * fires no event. The shell re-renders the routed component on navigation, so the value
 * is recomputed from `window.location` on every render and held in state only so that a
 * `popstate` while the screen is mounted also re-runs the queries.
 */
export function useInSeasonScope(): InSeasonScope {
  const [, setNavigations] = useState(0);

  useEffect(() => {
    const onPopState = (): void => setNavigations((count) => count + 1);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const search = typeof window === 'undefined' ? '' : window.location.search;
  return useMemo(() => readScope(search), [search]);
}

/** The explain state, following browser back and forward — `useInSeasonScope`'s shape. */
export function useExplainScope(): ExplainScope {
  const [, setNavigations] = useState(0);

  useEffect(() => {
    const onPopState = (): void => setNavigations((count) => count + 1);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const search = typeof window === 'undefined' ? '' : window.location.search;
  return useMemo(() => readExplain(search), [search]);
}

/** Open (or, with `null`, re-open the list of) the explain view — a history entry, like every scope change. */
export function openExplain(itemId: string | null): void {
  if (typeof window === 'undefined') return;
  navigateToScope(explainSearch(window.location.search, itemId));
}

/** Close the explain view. */
export function closeExplain(): void {
  if (typeof window === 'undefined') return;
  navigateToScope(closedExplainSearch(window.location.search));
}
